/**
 * Générateur — Utiliser un graphique de dépendance pour lire et interpréter des
 * valeurs (abscisse / ordonnée).
 * Attendu ID : 177 | Niveau : 4ème | Domaine : Fonctions (O14)
 *
 * Doc prof (Créé n°13 « La courbe de température ») : courbe de température d'un
 * patient de 6 h à 22 h. Plan de cartes validé en juillet 2026 (historique) :
 * une carte « La courbe de température », formats lecture à deux heures,
 * maximum, QCM multiple « à quelle(s) heure(s) », QCM de croissance, V/F palier.
 * La courbe est un SVG linéaire par morceaux (svgCourbe, fonctions.js, que 177
 * est seul à importer) attaché en elements_additionnels ; les questions ne
 * portent que sur des heures de relevé (toutes les 2 h).
 *
 * Revue 2026-09-23 (vague 3), décisions du coordinateur appliquées en mode
 * auto. Attendu de fin de 4e (Eduscol, attendus de fin d'année, p. 6-7) :
 * « Il utilise un graphique représentant la dépendance de deux grandeurs pour
 * lire et interpréter différentes valeurs sur l'axe des abscisses ou l'axe des
 * ordonnées. » Exemple de réussite : le four, « la température du four au bout
 * de 7 min ; le temps au bout duquel il atteint 110 °C », lu dans LES DEUX
 * sens, valeurs sur le quadrillage, aucun point marqué sur la courbe.
 *   - A1 GRAVE corrigé : températures en DEMI-DEGRÉS, quadrillage tous les
 *     0,5 °C, graduations numérotées aux degrés entiers : toute valeur
 *     demandée est sur une ligne, par construction (avant, grille de 0,5 °C et
 *     températures au dixième : 80,4 % des valeurs à lire entre deux lignes,
 *     96,4 % des questions « Lecture » en avaient au moins une). Quinze profils
 *     simples (montée et descente de pentes différentes ; l'heure du pic, 12,
 *     14 ou 16 h, tirée d'abord à parts égales) et huit profils à palier, en
 *     demi-degrés, décalés entre 36,5 et 40,5 °C.
 *   - Figure : points rouges aux heures de relevé RETIRÉS (ils montraient où
 *     lire) ; titres d'axes « Température (en °C) » et « Heure (en h) », plus
 *     de « °C » sous la pointe de la flèche ; attribut alt qui ne donne aucune
 *     valeur ; bloc <desc> retiré (aucun solveur ne le lisait).
 *   - Consigne commune : « donne la température d'un patient, en degrés
 *     Celsius, relevée de 6 h à 22 h » (la parenthèse « (en °C) » en phrase).
 *   - Lecture (temperature_lecture_texte_trou_saisie) et maximum
 *     (temperature_max_texte_trou_saisie) GARDÉS.
 *   - QCM multiple (temperature_heures_qcm_multiple) : « Sélectionne les deux
 *     heures où la température valait X °C. » (le nombre de réponses dit, plus
 *     de parenthèse) ; les deux bonnes heures ne sont JAMAIS symétriques
 *     autour du pic (100 % avant) et deux des trois mauvaises le sont (paire
 *     leurre) ; distracteurs nommés heure_voisine (une heure de relevé à côté
 *     d'une bonne heure) et valeur_voisine (une heure où la température vaut
 *     X ± 0,5 °C : une ligne de trop ou de moins sur l'axe des ordonnées).
 *   - NOUVEAU QCM (temperature_premiere_qcm_unique) : « À quelle heure la
 *     température a-t-elle atteint X °C pour la première fois ? », le sens
 *     ordonnée vers abscisse de l'exemple. Écart à la décision, mode auto, À
 *     ARBITRER : les distracteurs forment la grille { première fois, seconde
 *     fois } × { bonne ligne, ligne voisine } : seconde_occurrence,
 *     graduation_sautee (première heure où la température vaut X ± 0,5 °C) et
 *     les_deux (la seconde) ; axe_inverse, décidé, n'est pas codé : une erreur
 *     d'axe fait lire une température, jamais une heure, aucune option-heure
 *     ne la matérialise. Avec heure_voisine en troisième, la clé était l'heure
 *     du milieu de trois heures consécutives ; avec la grille, elle est la
 *     première ou la deuxième des quatre heures selon le sens de l'erreur de
 *     ligne. Dernière correction (coordinateur), mode auto, À ARBITRER : le
 *     SENS de la ligne voisine (au-dessus ou au-dessous) est tiré d'abord, à
 *     parts égales parmi ceux que le profil offre, puis la valeur ; la clé était
 *     la deuxième heure dans 63 % des tirages, 50 % après (elle reste l'une
 *     des deux plus tôt, c'est la question « pour la première fois ») ; les
 *     courbes à creux initial, trois tirages sur cinq, portent la clé jusqu'à
 *     14 h et 16 h (avant : 8, 10 ou 12 h seulement).
 *   - QCM de croissance (temperature_croissance_qcm) : la phrase qui décrivait
 *     la courbe est RETIRÉE (réussi sans la figure dans 100 % des tirages) ;
 *     « Entre quelles heures la température n'a-t-elle fait qu'augmenter ? »
 *     (mode auto : « n'a fait qu'augmenter » exclut la lecture « plus chaude à
 *     la fin qu'au début ») ; distracteurs phase_descendante (du pic à 22 h),
 *     toute_la_journee (de 6 h à 22 h), a_cheval_sur_le_pic (de 6 h à 2 h après
 *     le pic : le pic lu une graduation trop tard), jamais un sous-intervalle
 *     de la montée. Deux options commencent à 6 h sans finir à 22 h (avant :
 *     la clé était la seule, 100 %). Dernière correction (coordinateur), mode
 *     auto, À ARBITRER : la montée partait toujours de 6 h, la clé était donc
 *     encore le plus court des intervalles partant de 6 h, celui qui finit le
 *     plus tôt, et le seul qui finit où un autre commence (100 % chacun, sans
 *     la figure). Trois tirages sur cinq, la courbe commence par BAISSER jusqu'à
 *     un creux à 8 ou 10 h (PROFILS_CREUX, fonctions.js) : clé [creux ; pic],
 *     distracteurs depuis_le_debut [6 h ; pic], baisse_du_debut [6 h ; creux],
 *     a_cheval_sur_le_pic [creux ; pic + 2 h]. Treize stratégies sans la figure
 *     mesurées sur les textes des options : aucune au-dessus de 50 % (la plus
 *     haute, « l'option qui partage le plus d'extrémités avec les autres », à
 *     50 % pile ; « le plus court depuis le début » à 40 %). Trois sur cinq et
 *     non la moitié : à la moitié, trois stratégies restaient à 50 % pile, les
 *     courbes qui montent dès 6 h les réussissant toutes par construction (un
 *     intervalle faux y dépasse forcément le pic).
 *   - V/F (temperature_palier_vf) : trois natures de lignes (a augmenté, a
 *     diminué, est restée constante), quatre intervalles de 2 h différents
 *     dont celui du palier, une à trois lignes vraies (avant : toujours une),
 *     les trois natures présentes à chaque tirage ; lignes fausses nommées
 *     sens_inverse, palier_pour_variation, variation_pour_palier.
 *   - Second contexte (le four de l'exemple, 0 à 180 °C, minutes) : NON codé ;
 *     le moteur de courbe n'est pas générique (heures de relevé, axe en
 *     demi-degrés et consignes écrits pour la fièvre), à faire dans un lot à
 *     part.
 *   - Rappel brouillon : AUCUN (décision : lire un graphique se fait de tête).
 *   - Second chemin : chaque réponse est relue sur la courbe (valeurs aux
 *     heures de relevé et passages entre deux heures, sur la ligne brisée) et
 *     comparée à la construction, throw sinon. Aucun solveur test-side ne
 *     couvre 177 ; le test jetable tmp-revue-177 relit la figure décodée comme
 *     un élève (étiquettes des axes, sommets de la ligne brisée).
 *   - Contrat des distracteurs exporté (REGLES_DISTRACTEURS_QCM), codes dans
 *     _meta.params.distracteurs.
 *   - Les courbes à creux initial sont réservées à ces deux QCM : au QCM
 *     multiple, une valeur prise au début de la baisse le serait trois fois ;
 *     lecture, maximum et V/F n'en ont pas besoin.
 * Six formats (cinq avant), dont deux QCM uniques.
 */

import {
    randInt, randomChoice, randomChoiceMultiple, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';
import {
    fmtT, HOURS, PROFILS_SIMPLES, PROFILS_PALIER, PROFILS_CREUX, enDixiemes, decalagesPossibles,
    svgCourbe, imgElement, ALT_COURBE,
} from './fonctions';

const ATTENDU_ID = 177;
const L_TEMP = 'La courbe de température';
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });
/** Options + courbe attachée en image, avec son alt. */
const withCourbe = (label, temps) => ({
    ...opts(label),
    elements_additionnels: imgElement(svgCourbe(temps), 'medium', ALT_COURBE),
});

const CONSIGNE = '<p>Le graphique ci-dessous donne la température d’un patient, en degrés Celsius, relevée de 6 h à 22 h.</p>';

/**
 * Tire une courbe : l'heure du pic d'abord (12, 14 ou 16 h à parts égales,
 * parmi celles du pool), puis un profil de ce pic, décalé ; dixièmes de °C,
 * multiples de 5.
 */
const tirerCourbe = (pool) => {
    const heurePic = (p) => HOURS[p.indexOf(Math.max(...p))];
    const P = randomChoice([...new Set(pool.map(heurePic))]);
    const profil = randomChoice(pool.filter((p) => heurePic(p) === P));
    return { profil, temps: enDixiemes(profil, randomChoice(decalagesPossibles(profil))) };
};

/**
 * Le pool du QCM de croissance et du QCM « pour la première fois » : une courbe
 * qui commence par baisser (PROFILS_CREUX) trois tirages sur cinq, sinon une
 * courbe qui monte dès 6 h. Mode auto, À ARBITRER : « environ la moitié »
 * demandée ; à moitié exacte, trois stratégies sans la figure restaient à 50 %
 * pile au QCM de croissance, les profils simples les réussissant tous par
 * construction.
 */
const poolCreuxOuSimple = () => (randInt(1, 5) <= 3 ? PROFILS_CREUX : PROFILS_SIMPLES);

/** Indice / heure / valeur du maximum ; throw s'il n'est pas atteint à une seule heure. */
const pic = (temps) => {
    const max = Math.max(...temps);
    if (temps.filter((t) => t === max).length !== 1) throw new Error(`gen_177 : maximum non unique ${temps}`);
    const idx = temps.indexOf(max);
    return { idx, heure: HOURS[idx], t10: max };
};

// ═════════ Lecture de la ligne brisée (second chemin) ═════════

/** Les instants (en heures, fractions comprises) où la ligne brisée vaut X. */
const instantsOu = (temps, X) => {
    const out = [];
    for (let i = 0; i < temps.length; i++) {
        if (temps[i] === X) out.push(HOURS[i]);
        if (i < temps.length - 1 && (temps[i] - X) * (temps[i + 1] - X) < 0) {
            out.push(HOURS[i] + ((X - temps[i]) / (temps[i + 1] - temps[i])) * (HOURS[i + 1] - HOURS[i]));
        }
    }
    return out;
};
/** La nature de la variation entre deux heures de relevé consécutives. */
const nature = (temps, i) => {
    if (temps[i + 1] > temps[i]) return 'augmente';
    if (temps[i + 1] < temps[i]) return 'diminue';
    return 'constante';
};

// ─── 1. Lire la température à deux heures ─────────────────────
const genLectureTrous = () => {
    const { temps } = tirerCourbe([...PROFILS_SIMPLES, ...PROFILS_PALIER]);
    const [i1, i2] = randomChoiceMultiple(HOURS.map((_, i) => i), 2).sort((a, b) => a - b);
    if (temps[i1] % 5 !== 0 || temps[i2] % 5 !== 0) throw new Error('gen_177 : valeur hors du quadrillage');
    const phrase = `À ${HOURS[i1]} h, la température était de {{${fmtT(temps[i1])}}} °C. `
        + `À ${HOURS[i2]} h, elle était de {{${fmtT(temps[i2])}}} °C.`;
    const payload = makeTexteTrou(phrase,
        `${CONSIGNE}<p><b>Lis sur le graphique la température à ${HOURS[i1]} h, puis à ${HOURS[i2]} h.</b></p>`,
        { mode: 'saisie', ...withCourbe(L_TEMP, temps) });
    payload._meta.params = { temps, heures: [HOURS[i1], HOURS[i2]] };
    return payload;
};

// ─── 2. Température maximale et son heure ─────────────────────
const genMaxTrous = () => {
    const { temps } = tirerCourbe([...PROFILS_SIMPLES, ...PROFILS_PALIER]);
    const p = pic(temps);
    const phrase = `La température maximale est {{${fmtT(p.t10)}}} °C, atteinte à {{${p.heure}}} h.`;
    const payload = makeTexteTrou(phrase,
        `${CONSIGNE}<p><b>Quelle a été la température maximale, et à quelle heure a-t-elle été atteinte ?</b></p>`,
        { mode: 'saisie', ...withCourbe(L_TEMP, temps) });
    payload._meta.params = { temps, pic: p.heure };
    return payload;
};

// ─── 3. Les deux heures où T = X (QCM multiple) ───────────────
/**
 * Les valeurs X atteintes à exactement deux heures de relevé intérieures
 * (8 h à 20 h), NON symétriques autour du pic, avec leurs distracteurs nommés
 * et au moins une paire de distracteurs symétrique autour du pic.
 */
const candidatsMulti = (temps) => {
    const P = pic(temps).heure;
    const out = [];
    [...new Set(temps)].forEach((X) => {
        const bonnes = HOURS.filter((h, i) => temps[i] === X);
        if (bonnes.length !== 2 || bonnes[0] < 8 || bonnes[1] > 20) return;
        if (bonnes[0] + bonnes[1] === 2 * P) return;
        const code = (h) => {
            if (bonnes.includes(h)) return null;
            if (Math.abs(temps[HOURS.indexOf(h)] - X) === 5) return 'valeur_voisine';
            if (bonnes.some((b) => Math.abs(h - b) === 2)) return 'heure_voisine';
            return null;
        };
        const nommees = HOURS.filter((h) => code(h)).map((h) => ({ h, code: code(h) }));
        const paires = [];
        for (let d = 2; d <= 16; d += 2) {
            const a = nommees.find((c) => c.h === P - d); const b = nommees.find((c) => c.h === P + d);
            if (a && b) paires.push([a, b]);
        }
        if (paires.length && nommees.length >= 3) out.push({ X, bonnes, nommees, paires, P });
    });
    return out;
};

const assemblerMulti = (temps, c, paire, troisieme) => {
    // Second chemin : sur la ligne brisée, la température vaut X à exactement
    // deux instants, les deux bonnes heures ; à aucune heure proposée fausse.
    const instants = instantsOu(temps, c.X);
    if (instants.length !== 2 || !c.bonnes.every((h) => instants.includes(h))) throw new Error(`gen_177 : multi ${c.X} ${instants}`);
    const faux = [...paire, troisieme];
    if (faux.some((f) => instants.includes(f.h))) throw new Error('gen_177 : multi, un distracteur est juste');
    const bonnes = c.bonnes.map((h) => `${h} h`);
    const choix = shuffle([...bonnes, ...faux.map((f) => `${f.h} h`)]);
    const payload = makeQcm(choix, bonnes,
        `${CONSIGNE}<p><b>Sélectionne les deux heures où la température valait ${fmtT(c.X)} °C.</b></p>`,
        withCourbe(L_TEMP, temps), true);
    payload._meta.params = {
        temps, X: c.X, bonnes: c.bonnes, pic: c.P, paire_leurre: paire.map((f) => f.h),
        distracteurs: faux.map((f) => f.code),
    };
    return payload;
};

const genHeuresQcmMulti = () => {
    for (let essai = 0; essai < 40; essai++) {
        const { temps } = tirerCourbe(PROFILS_SIMPLES);
        const cands = candidatsMulti(temps);
        if (!cands.length) continue;
        const c = randomChoice(cands);
        const paire = randomChoice(c.paires);
        const reste = c.nommees.filter((n) => !paire.includes(n));
        if (!reste.length) continue;
        return assemblerMulti(temps, c, paire, randomChoice(reste));
    }
    // Repli déterministe vérifié : premier profil, sans décalage ; X = 37,5 °C à
    // 10 h et 18 h (pic 12 h), paire leurre 8 h et 16 h, troisième 12 h.
    const temps = enDixiemes(PROFILS_SIMPLES[0], 0);
    const c = candidatsMulti(temps).find((x) => x.X === 375);
    if (!c) throw new Error('gen_177 : repli multi');
    const paire = c.paires.find(([a, b]) => a.h === 8 && b.h === 16);
    return assemblerMulti(temps, c, paire, c.nommees.find((n) => n.h === 12));
};

// ─── 4. Première fois que T = X (QCM unique, ordonnée → abscisse) ───
/**
 * Grille { première, seconde fois } × { X, X ± 0,5 °C } : X et la valeur
 * voisine Y sont chacune atteintes EXACTEMENT deux fois sur la ligne brisée
 * (montée puis descente), à des heures de relevé ; X pour la première fois à
 * 8 h au plus tôt. Lu sur les passages de la ligne (instantsOu) et non sur les
 * seuls relevés : sur une courbe qui commence par baisser, une valeur peut être
 * croisée entre deux relevés au début de la journée.
 */
const candidatsPremiere = (temps) => {
    const P = pic(temps).heure;
    const deuxFois = (v) => {
        const ins = instantsOu(temps, v);
        return ins.length === 2 && ins.every((h) => HOURS.includes(h)) && ins[0] >= 8 && ins[0] < P && ins[1] > P ? ins : null;
    };
    const out = [];
    [...new Set(temps)].forEach((X) => {
        const o = deuxFois(X);
        if (!o) return;
        [X - 5, X + 5].forEach((Y) => {
            const q = deuxFois(Y);
            if (q) out.push({ X, Y, cle: o[0], seconde: o[1], grad: q[0], lesDeux: q[1] });
        });
    });
    return out;
};

const assemblerPremiere = (temps, c) => {
    // Second chemin : le premier instant où la ligne brisée vaut X est la clé ;
    // le second, la seconde occurrence ; Y est atteinte aux deux autres heures.
    const ix = instantsOu(temps, c.X); const iy = instantsOu(temps, c.Y);
    if (ix.length !== 2 || ix[0] !== c.cle || ix[1] !== c.seconde || iy.length !== 2 || iy[0] !== c.grad || iy[1] !== c.lesDeux) {
        throw new Error(`gen_177 : première fois ${c.X} ${ix} ${iy}`);
    }
    const options = [
        { h: c.cle, code: null },
        { h: c.seconde, code: 'seconde_occurrence' },
        { h: c.grad, code: 'graduation_sautee' },
        { h: c.lesDeux, code: 'les_deux' },
    ];
    if (new Set(options.map((o) => o.h)).size !== 4) throw new Error('gen_177 : première fois, heures en double');
    const choix = options.map((o) => `${o.h} h`);
    const payload = makeQcm(choix, choix[0],
        `${CONSIGNE}<p><b>À quelle heure la température a-t-elle atteint ${fmtT(c.X)} °C pour la première fois ?</b></p>`,
        withCourbe(L_TEMP, temps));
    payload._meta.params = { temps, X: c.X, Y: c.Y, distracteurs: options.slice(1).map((o) => o.code) };
    return payload;
};

// Le SENS de la ligne voisine d'abord (revue 2026-09-23, dernière correction) :
// au-dessus (Y = X + 0,5 °C), la clé est la plus tôt des quatre heures ;
// au-dessous, la deuxième. Chaque profil n'offre souvent qu'un sens par valeur
// (au-dessus pour X, au-dessous pour la valeur suivante) : tirer la valeur puis
// la voisine donnait la clé deuxième dans 63 % des tirages. Le sens est tiré à
// parts égales parmi ceux que le profil offre, puis une valeur de ce sens.
const genPremiereQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const { temps } = tirerCourbe(poolCreuxOuSimple());
        const cands = candidatsPremiere(temps);
        if (!cands.length) continue;
        const sens = randomChoice([...new Set(cands.map((c) => Math.sign(c.Y - c.X)))]);
        return assemblerPremiere(temps, randomChoice(cands.filter((c) => Math.sign(c.Y - c.X) === sens)));
    }
    // Repli déterministe vérifié : premier profil, sans décalage ; 37,5 °C à
    // 10 h puis 18 h, 37 °C à 8 h puis 20 h.
    const temps = enDixiemes(PROFILS_SIMPLES[0], 0);
    const c = candidatsPremiere(temps).find((x) => x.X === 375 && x.Y === 370);
    if (!c) throw new Error('gen_177 : repli première fois');
    return assemblerPremiere(temps, c);
};

// ─── 5. Entre quelles heures la température n'a fait qu'augmenter ? ───
// Trois tirages sur cinq, la courbe commence par BAISSER jusqu'à un creux C (8
// ou 10 h) : la clé est [C ; pic] et les distracteurs deviennent depuis_le_debut
// [6 h ; pic] (la baisse du début incluse, plus long que la clé),
// baisse_du_debut [6 h ; C] (la baisse prise pour la hausse, sur le premier
// morceau) et a_cheval_sur_le_pic [C ; pic + 2 h]. Avant, la montée partait
// toujours de 6 h : la clé était, par construction, le plus court des
// intervalles partant de 6 h, celui qui finit le plus tôt et le seul qui finit
// où un autre commence (100 % chacun, sans regarder la figure).
const genCroissanceQcm = () => {
    const { temps } = tirerCourbe(poolCreuxOuSimple());
    const P = pic(temps).heure;
    let iCreux = 0;
    while (iCreux < HOURS.length - 1 && temps[iCreux + 1] < temps[iCreux]) iCreux += 1;
    const C = HOURS[iCreux];                         // 6 h pour une courbe qui monte dès le début
    const options = C > 6 ? [
        { a: C, b: P, code: null },
        { a: 6, b: P, code: 'depuis_le_debut' },
        { a: 6, b: C, code: 'baisse_du_debut' },
        { a: C, b: P + 2, code: 'a_cheval_sur_le_pic' },
    ] : [
        { a: 6, b: P, code: null },
        { a: 6, b: P + 2, code: 'a_cheval_sur_le_pic' },
        { a: P, b: 22, code: 'phase_descendante' },
        { a: 6, b: 22, code: 'toute_la_journee' },
    ];
    // Second chemin : la clé ne fait que monter et ne se prolonge ni avant ni
    // après ; chaque distracteur contient au moins une baisse (donc jamais un
    // morceau de la montée, qui serait juste lui aussi).
    const monte = (a, b) => HOURS.every((h, i) => h < a || h >= b || nature(temps, i) === 'augmente');
    if (!monte(C, P) || (C > 6 && monte(C - 2, P)) || monte(C, P + 2)) throw new Error(`gen_177 : croissance ${temps}`);
    if (options.slice(1).some((o) => monte(o.a, o.b))) throw new Error('gen_177 : croissance, un distracteur est juste');
    const choix = options.map((o) => `Entre ${o.a} h et ${o.b} h`);
    const payload = makeQcm(choix, choix[0],
        `${CONSIGNE}<p><b>Entre quelles heures la température n’a-t-elle fait qu’augmenter ?</b></p>`,
        withCourbe(L_TEMP, temps));
    payload._meta.params = { temps, creux: C, pic: P, distracteurs: options.slice(1).map((o) => o.code) };
    return payload;
};

// ─── 6. Augmenté, diminué, resté constant ? (V/F) ─────────────
const PHRASE_NATURE = { augmente: 'a augmenté', diminue: 'a diminué', constante: 'est restée constante' };
const NATURES = Object.keys(PHRASE_NATURE);
/** Le code d'une ligne fausse : la nature écrite au lieu de la vraie. */
const codeFausse = (vraie, ecrite) => {
    if (vraie === 'constante') return 'variation_pour_palier';
    if (ecrite === 'constante') return 'palier_pour_variation';
    return 'sens_inverse';
};

const assemblerVf = (temps, lignes) => {
    // Second chemin : chaque ligne relue sur la courbe (valeurs aux deux heures).
    lignes.forEach((l) => {
        const i = HOURS.indexOf(l.h1);
        const reelle = temps[i + 1] > temps[i] ? 'augmente' : temps[i + 1] < temps[i] ? 'diminue' : 'constante';
        if ((reelle === l.ecrite) !== l.vraie) throw new Error(`gen_177 : V/F ${l.h1} h`);
    });
    const payload = makeVraiFaux(
        lignes.map((l) => `Entre ${l.h1} h et ${l.h1 + 2} h, la température ${PHRASE_NATURE[l.ecrite]}.`),
        lignes.map((l) => (l.vraie ? 0 : 1)),
        `${CONSIGNE}<p><b>Vrai ou faux ?</b></p>`,
        withCourbe(L_TEMP, temps));
    payload._meta.params = {
        temps, lignes: lignes.map((l) => `${l.h1}-${l.h1 + 2} ${l.ecrite} ${l.vraie ? 'V' : 'F'}`),
        distracteurs: lignes.filter((l) => !l.vraie).map((l) => l.code),
    };
    return payload;
};

const genPalierVF = () => {
    for (let essai = 0; essai < 80; essai++) {
        const { temps } = tirerCourbe(PROFILS_PALIER);
        const iPalier = HOURS.findIndex((_, i) => i < HOURS.length - 1 && nature(temps, i) === 'constante');
        const autres = shuffle(HOURS.slice(0, -1).map((_, i) => i).filter((i) => i !== iPalier)).slice(0, 3);
        const indices = shuffle([iPalier, ...autres]);
        const nbVraies = randInt(1, 3);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = indices.map((i, k) => {
            const reelle = nature(temps, i);
            const ecrite = verites[k] ? reelle : randomChoice(NATURES.filter((n) => n !== reelle));
            return { h1: HOURS[i], ecrite, vraie: verites[k], code: verites[k] ? null : codeFausse(reelle, ecrite) };
        });
        if (new Set(lignes.map((l) => l.ecrite)).size !== 3) continue;   // les trois natures écrites
        return assemblerVf(temps, lignes);
    }
    // Repli déterministe vérifié : premier profil à palier (palier 10 h – 12 h),
    // sans décalage ; deux vraies, deux fausses, les trois natures.
    const temps = enDixiemes(PROFILS_PALIER[0], 0);
    return assemblerVf(temps, [
        { h1: 10, ecrite: 'constante', vraie: true, code: null },
        { h1: 6, ecrite: 'augmente', vraie: true, code: null },
        { h1: 16, ecrite: 'augmente', vraie: false, code: 'sens_inverse' },
        { h1: 12, ecrite: 'diminue', vraie: false, code: 'sens_inverse' },
    ]);
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'températures en demi-degrés (36,5 à 40,5 °C), quadrillage tous les 0,5 °C : toute valeur demandée est sur une ligne ; questions aux heures de relevé seulement (6 h à 22 h, toutes les 2 h)',
        'chaque réponse est relue sur la ligne brisée (instants où elle vaut X, variations entre deux relevés), throw si elle ne colle pas',
        'chaque distracteur est une erreur NOMMÉE (codes dans _meta.params.distracteurs)',
    ],
    temperature_heures_qcm_multiple: {
        support: 'profils simples ; X atteinte à exactement deux heures de relevé, de 8 h à 20 h, NON symétriques autour du pic',
        familles: {
            heure_voisine: 'une heure de relevé à 2 h d\'une bonne heure : la ligne verticale d\'à côté',
            valeur_voisine: 'une heure où la température vaut X ± 0,5 °C : une ligne de trop ou de moins sur l\'axe des ordonnées',
        },
        composition: 'cinq choix, deux bonnes heures ; parmi les trois mauvaises, une paire symétrique autour du pic (l\'élève qui cherche la symétrie s\'y trompe), la troisième tirée dans les autres heures nommées',
    },
    temperature_premiere_qcm_unique: {
        support: 'profils à creux initial trois tirages sur cinq, sinon profils simples ; X et Y = X ± 0,5 °C atteintes chacune exactement deux fois sur la ligne brisée (montée puis descente) à des heures de relevé ; X pour la première fois à 8 h au plus tôt',
        grille: '{ première fois, seconde fois } × { X, Y } : la clé et trois erreurs nommées',
        tirage: 'le sens de la ligne voisine d\'abord, à parts égales parmi ceux que le profil offre (au-dessus : la clé est la plus tôt des quatre heures ; au-dessous : la deuxième), puis une valeur X de ce sens ; la clé tombe de 8 h à 16 h',
        familles: {
            seconde_occurrence: 'la seconde heure où la température vaut X (la descente)',
            graduation_sautee: 'la première heure où la température vaut Y : une ligne sautée ou ajoutée sur l\'axe des ordonnées',
            les_deux: 'la seconde heure où la température vaut Y',
        },
        non_codee: {
            axe_inverse: 'décidée, non codée : une erreur d\'axe fait lire une température, jamais une heure, aucune option-heure ne la matérialise (mode auto, À ARBITRER)',
        },
    },
    temperature_croissance_qcm: {
        support: 'trois tirages sur cinq, une courbe qui commence par baisser jusqu\'à un creux C (8 ou 10 h), pic P à 14, 16 ou 18 h, clé « Entre C h et P h » ; sinon une courbe qui monte dès 6 h, pic P à 12, 14 ou 16 h, clé « Entre 6 h et P h »',
        familles: {
            depuis_le_debut: 'Entre 6 h et P h (courbe à creux) : la baisse du début incluse, la montée lue depuis le premier relevé',
            baisse_du_debut: 'Entre 6 h et C h (courbe à creux) : la baisse prise pour la hausse, sur le premier morceau',
            a_cheval_sur_le_pic: 'Entre C h (ou 6 h) et P + 2 h : le pic lu une graduation trop tard',
            phase_descendante: 'Entre P h et 22 h (courbe simple) : la baisse prise pour la hausse',
            toute_la_journee: 'Entre 6 h et 22 h (courbe simple) : la journée entière',
        },
        strategies_sans_figure: 'mesurées dans tmp-revue-177 sur les seuls textes des options (plus court depuis le début, fin la plus tôt, fin partagée, plus court finissant avant 22 h, etc.) : aucune au-dessus de 50 % ; avant, quatre à 100 %',
        garantie: 'jamais un sous-intervalle de la montée (deux bonnes réponses) ; la clé ne se prolonge ni avant ni après ; chaque distracteur contient au moins une baisse (relu)',
    },
    temperature_palier_vf: {
        support: 'profils à palier ; quatre intervalles de 2 h différents dont celui du palier, une à trois lignes vraies, les trois natures écrites à chaque tirage',
        familles: {
            sens_inverse: 'a augmenté pour a diminué, ou l\'inverse',
            palier_pour_variation: 'est restée constante sur un intervalle où elle varie',
            variation_pour_palier: 'a augmenté ou a diminué sur le palier',
        },
    },
};

// ─── Registry & Export ────────────────────────────────────────

const GENERATORS = {
    temperature_lecture_texte_trou_saisie: genLectureTrous,
    temperature_max_texte_trou_saisie: genMaxTrous,
    temperature_heures_qcm_multiple: genHeuresQcmMulti,
    temperature_premiere_qcm_unique: genPremiereQcm,
    temperature_croissance_qcm: genCroissanceQcm,
    temperature_palier_vf: genPalierVF,
};

export const CATEGORIES = [
    {
        id: 'temperature',
        label: L_TEMP,
        formatsList: [
            { key: 'temperature_lecture_texte_trou_saisie', label: 'Lecture (2 heures)' },
            { key: 'temperature_max_texte_trou_saisie', label: 'Maximum' },
            { key: 'temperature_heures_qcm_multiple', label: 'QCM multiple' },
            { key: 'temperature_premiere_qcm_unique', label: 'QCM première fois' },
            { key: 'temperature_croissance_qcm', label: 'QCM croissance' },
            { key: 'temperature_palier_vf', label: 'Vrai/Faux' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
